import { Injectable } from '@angular/core';
import Swal, { SweetAlertIcon, SweetAlertPosition } from 'sweetalert2';
import { ImagekitUrlAppenderPipe } from '../pipes/imagekit-url-appender.pipe';
import { AssetsImagekitUrlAppenderPipe } from '../pipes/asset-imagekit-url-appender.pipe';

@Injectable({ providedIn: 'root' })
export class ToastAlertService {

  constructor(private imagekitUrlAppenderPipe: ImagekitUrlAppenderPipe, private assetsImagekitUrlAppender: AssetsImagekitUrlAppenderPipe) { }

  /**
   * Show Toast
   * @param type
   * @param title
   * @param side
   * @param time
   * @param showProgressBar
   */
  showToast(title: string, type: SweetAlertIcon = "success", side: SweetAlertPosition = 'top-end', time: number = 3500, showProgressBar: boolean = true) {
    const Toast = Swal.mixin({
      toast: true,
      position: side ? side : 'top-end',
      showConfirmButton: false,
      timer: time,
      showCloseButton: true,
      timerProgressBar: showProgressBar ? showProgressBar : true,
      didOpen: (toast) => {
        toast.addEventListener('mouseenter', Swal.stopTimer)
        toast.addEventListener('mouseleave', Swal.resumeTimer)
      }
    })
    Toast.fire({
      icon: type,
      title: title,
    })
  }


  /**
   * Show a centered (non-toast) SweetAlert2 modal, dismissed ONLY by the
   * explicit "Ok" button. Used for warnings that must be seen in front of
   * the user rather than as a small corner notification (e.g. blocked
   * forward tab navigation, IncompleteStepForwardGuard).
   *
   * Fix (2026-08-24): added allowOutsideClick/allowEscapeKey: false so the
   * modal itself can't be silently dismissed by a backdrop click/Escape.
   *
   * Second fix, same day, confirmed via live testing: that alone did NOT
   * stop the profile-completion nav-links tab strip from staying clickable
   * underneath the (visually dimmed) backdrop -- SweetAlert2's backdrop
   * dims the page but doesn't reliably block pointer-events on every
   * element behind it in this app. Now explicitly toggling a
   * `body.tabs-frozen-during-warning` class (see `styles.scss`) that
   * disables pointer-events on all 9 nav-links variants' tab links for the
   * exact duration the modal is open -- added before `Swal.fire()`, removed
   * once its promise resolves (Ok clicked), covering all 8 stakeholder
   * types uniformly since this one shared method backs every one of them.
   * @param message
   * @param icon
   */
  showCenteredAlert(message: string, icon: SweetAlertIcon = 'warning') {
    document.body.classList.add('tabs-frozen-during-warning');
    Swal.fire({
      icon,
      text: message,
      confirmButtonText: 'Ok',
      allowOutsideClick: false,
      allowEscapeKey: false,
      // SAN-1004 — didDestroy, not .then(): a later Swal.fire() (e.g. an
      // auto-save toast) destroys this popup without resolving its promise,
      // which left the tab strip frozen and faded until reload.
      didDestroy: () => {
        document.body.classList.remove('tabs-frozen-during-warning');
      },
    })
  }

  /**
   * Show a centered two-choice SweetAlert2 confirm ("Stay" / "Save & Exit")
   * used to warn a user who is about to leave the profile-completion area
   * before ever submitting (SAN-499). "Stay" is the primary/default/focused
   * choice (`focusCancel: true`, `reverseButtons: true`); "Save & Exit"
   * flushes the current step's pending auto-save (via
   * `UnsavedProfileLeaveGuard`'s `flushPendingAutoSave()` call, the same
   * PATCH each step's own auto-save already uses) and proceeds with the
   * navigation — renamed from "Discard" so the label matches what actually
   * happens, since it does persist whatever's in the fields, not throw it
   * away. Backdrop click / Escape both resolve via SweetAlert2's default
   * dismiss (cancel) path, i.e. "Stay".
   * @param message
   * @returns a Promise resolving `true` only when "Save & Exit" was chosen
   * (safe to proceed with navigation), `false` for "Stay" or any other
   * dismissal.
   */
  showLeaveConfirm(message: string): Promise<boolean> {
    return Swal.fire({
      icon: 'warning',
      title: 'Are you sure you want to leave?',
      text: message,
      showCancelButton: true,
      confirmButtonText: 'Save & Exit',
      cancelButtonText: 'Stay',
      focusCancel: true,
      reverseButtons: true
    }).then((result) => !!result.isConfirmed);
  }

  showHtmlToast(title: string, image: string, url: string, side: SweetAlertPosition = 'top-end', time: number = 10000, showProgressBar: boolean = true) {
    const imageUrl = !image ? '/assets/images/upload-image-placeholder.png' :this.imagekitUrlAppenderPipe.transform(image)+ '?tr=w-50,h-50,cm-pad_resize'
    const Toast = Swal.mixin({
      toast: true,
      position: side ? side : 'top-end',
      showConfirmButton: false,
      customClass: 'custom-swal-notification',
      timer: time,
      showCloseButton: true,
      timerProgressBar: showProgressBar ? showProgressBar : true,
      html: `
      <div class="d-flex align-items-center">
        <img src=${imageUrl} style="height: 50px;" class="me-3 rounded"/>

        <div class="d-flex flex-column">
          ${title}
          <a href="${url ?  url : ''}" class="${url? 'btn btn-sm btn-primary': 'd-none'}">
            View
          </a>
        </div>
      </div>
      `,

      didOpen: (toast) => {
        toast.addEventListener('mouseenter', Swal.stopTimer)
        toast.addEventListener('mouseleave', Swal.resumeTimer)
      }
    })

    Toast.fire({
      // icon: type,
      // title: title,
    })
  }

}
